import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddLinkOutlined from '@mui/icons-material/AddLinkOutlined';
import Close from '@mui/icons-material/Close';
import TestLinkBuilderPanel from './TestLinkBuilderPanel';
import { ENTITY_PARAM_ORDER, type CustomParam, type EntityParamKey } from './buildTestLink';
import { loadState, type BuilderState } from './storage';

/** What a screen can hand the builder: the entity it shows, and optionally where to test it. */
export interface TestLinkPrefill {
  siteId?: number | null;
  domainId?: number | null;
  path?: string;
  /** The params to start with, e.g. `{ sequence_id: 12190 }`. Replaces the remembered ones. */
  entities?: Partial<Record<EntityParamKey, number | null>>;
  custom?: CustomParam[];
}

/**
 * The form a dialog opens with. The remembered domain and path carry over — they are about where
 * you test, not what — while the params come from the screen. The remembered site is dropped when
 * the screen hands over a group or sequence, so the builder can take that entity's own site.
 */
export function prefillState(prefill: TestLinkPrefill, saved: BuilderState = loadState()): BuilderState {
  const entities: BuilderState['entities'] = {};
  ENTITY_PARAM_ORDER.forEach((key) => {
    if (prefill.entities && key in prefill.entities) entities[key] = prefill.entities[key] ?? null;
  });
  const hasEntities = Object.keys(entities).length > 0;
  const ownsSite = entities.lpgid != null || entities.sequence_id != null;
  return {
    ...saved,
    siteId: prefill.siteId !== undefined ? prefill.siteId : ownsSite ? null : saved.siteId,
    domainId: prefill.domainId !== undefined ? prefill.domainId : saved.domainId,
    path: prefill.path ?? saved.path,
    entities: hasEntities ? entities : saved.entities,
    custom: prefill.custom ?? (hasEntities ? [] : saved.custom),
  };
}

export interface TestLinkBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  prefill?: TestLinkPrefill;
}

/**
 * The builder as a dialog, for any screen: pass the entity ids it shows as `prefill`. It starts
 * fresh each time it opens and does not overwrite the Tools page's remembered form; links it
 * copies or opens still join the shared recent list.
 */
export default function TestLinkBuilderDialog({ open, onClose, prefill = {} }: TestLinkBuilderDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      scroll='paper'
      aria-labelledby='test-link-dialog-title'
    >
      <DialogTitle id='test-link-dialog-title' sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <AddLinkOutlined color='action' />
        Test link
        <IconButton aria-label='Close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        {open && <TestLinkBuilderPanel initialState={prefillState(prefill)} variant='dialog' inferSite />}
      </DialogContent>
    </Dialog>
  );
}

/** A drop-in trigger for a screen's header or toolbar: the link icon, opening the dialog with `prefill`. */
export function TestLinkButton({
  prefill,
  label = 'Build a test link',
}: {
  prefill?: TestLinkPrefill;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title={label}>
        <IconButton aria-label={label} onClick={() => setOpen(true)}>
          <AddLinkOutlined />
        </IconButton>
      </Tooltip>
      <TestLinkBuilderDialog open={open} onClose={() => setOpen(false)} prefill={prefill} />
    </>
  );
}
